import React, { useState, useEffect, useRef } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useParams, Navigate } from "react-router-dom";
import { PageTransition } from "../components/common/PageTransition";
import { GalleryHeader } from "../components/gallery/GalleryHeader";
import { ImageGallery } from "../components/gallery/ImageGallery";
import { LightboxViewer } from "../components/gallery/LightboxViewer";
import { useGallery, usePublicGalleries } from "../hooks/useGalleries";
import { useRecordAlbumView } from "../hooks/useViews";
import { trackGalleryViewEnhanced, trackImageClickEnhanced, trackLightboxView } from "../utils/analytics";
import { SEOHead } from "../components/seo/SEOHead";
import {
  generateGalleryStructuredData,
  generateOrganizationStructuredData,
  generateBreadcrumbStructuredData,
  getFullUrl,
} from "../utils/seo";
import { Breadcrumbs } from "../components/common/Breadcrumbs";
import { RelatedGalleries } from "../components/gallery/RelatedGalleries";

export const GalleryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: gallery, isLoading, error } = useGallery(id);
  const { data: allGalleries } = usePublicGalleries(); // For related galleries
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const recordAlbumView = useRecordAlbumView();
  const viewRecorded = useRef(false);

  // Record gallery view when gallery loads
  useEffect(() => {
    if (gallery && id && !viewRecorded.current) {
      viewRecorded.current = true;
      recordAlbumView.mutate(id, {
        onError: (error) => {
          // Silently fail - view tracking is not critical
          console.warn("Failed to record gallery view:", error);
        },
      });
      // Track in GA4 with enhanced data
      trackGalleryViewEnhanced(
        id,
        gallery.title,
        gallery.images?.length,
        gallery.category
      );
    }
  }, [gallery, id, recordAlbumView]);

  const handleImageClick = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    // Track image click in GA4 with enhanced data
    if (gallery?.images?.[index]) {
      trackImageClickEnhanced(
        gallery.images[index].id,
        gallery.id,
        index,
        gallery.images[index].caption
      );
    }
  };

  const handleLightboxClose = () => {
    setLightboxOpen(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <PageTransition>
        <Box
          sx={{
            minHeight: "100vh",
            pt: { xs: "80px", md: "80px" },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      </PageTransition>
    );
  }

  // Error state - redirect to galleries
  if (error || !gallery) {
    return <Navigate to="/galleries" replace />;
  }

  // Generate SEO data
  const galleryImages = gallery.images?.map((img) => img.url) || [];
  const galleryImage = galleryImages[0]; // Use first image as cover
  const galleryDescription = gallery.description || `View ${gallery.title} photography gallery by Mariah Blue`;
  
  // Get current date for structured data (since gallery object doesn't include dates)
  const currentDate = new Date().toISOString();
  
  const structuredData = [
    generateOrganizationStructuredData(getFullUrl("/")),
    generateGalleryStructuredData(
      gallery.title,
      galleryDescription,
      galleryImages,
      currentDate, // Use current date as fallback
      currentDate
    ),
    generateBreadcrumbStructuredData([
      { name: "Home", url: getFullUrl("/") },
      { name: "Galleries", url: getFullUrl("/galleries") },
      { name: gallery.title, url: getFullUrl(`/gallery/${gallery.id}`) },
    ]),
  ];
  
  const breadcrumbItems = [
    { label: "Home", path: "/" },
    { label: "Galleries", path: "/galleries" },
    { label: gallery.title, path: `/gallery/${gallery.id}` },
  ];

  return (
    <PageTransition>
      <SEOHead
        title={gallery.title}
        description={galleryDescription}
        image={galleryImage}
        url={getFullUrl(`/gallery/${gallery.id}`)}
        type="website"
        structuredData={structuredData}
      />
      <Box
        component="article"
        sx={{
          minHeight: "100vh",
          pt: 0,
          mt: 0,
        }}
      >
        {/* Breadcrumbs */}
        <Breadcrumbs items={breadcrumbItems} />
        
        {/* Header Section */}
        <Box
          component="header"
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            pt: 0,
            pb: { xs: 0.5, md: 1 },
            maxWidth: "1200px",
            mx: "auto",
            m: 0,
          }}
        >
          <Box sx={{ position: "relative", mb: 0, pt: 0, m: 0, zIndex: 1200 }}>
            <GalleryHeader
              title={gallery.title}
              description={gallery.description}
            />
          </Box>
        </Box>

        {/* Image Gallery - Full Width */}
        <Box
          component="section"
          aria-label={`${gallery.title} gallery images`}
          sx={{
            width: "100%",
            px: { xs: 0, md: 0 }, // No horizontal padding for full-width images
          }}
        >
          <ImageGallery
            images={gallery.images}
            onImageClick={handleImageClick}
          />
        </Box>

        {/* Related Galleries */}
        {allGalleries && allGalleries.length > 0 && (
          <RelatedGalleries
            currentGalleryId={gallery.id}
            currentCategory={gallery.category}
            allGalleries={allGalleries}
            maxGalleries={4}
          />
        )}

        {/* Lightbox */}
        <LightboxViewer
          images={gallery.images}
          open={lightboxOpen}
          index={lightboxIndex}
          onClose={handleLightboxClose}
          galleryId={gallery.id}
        />
      </Box>
    </PageTransition>
  );
};
